import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Table, Badge, Alert, Spinner, Button, Form, Modal } from 'react-bootstrap';
import { formatDate, formatDateTime } from '../dateFormat';

// Local chart-row shape — intentionally NOT the shared types/scouting ScoutingRecord
// (different concept: aggregated infestation counts, not a scouting record).
interface ReconciliationChartRow {
  pestInfestation: number;
  insectCount: number;
  date: string;
}

interface Comment {
  text: string;
  author: string;
  date: string;
}

interface Reconciliation {
  _id: string;
  blockId: string;
  date: string;
  variance: {
    pestInfestation: number;
    insectCount: number;
  };
  status: string;
  comments: Comment[];
  internalRecordId: ReconciliationChartRow | null;
  externalRecordId: ReconciliationChartRow | null;
}

const ReconciliationDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [records, setRecords] = useState<Reconciliation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal State
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedRec, setSelectedRec] = useState<Reconciliation | null>(null);
  const [commentText, setCommentText] = useState('');
  const [savingComment, setSavingComment] = useState(false);

  useEffect(() => {
    fetchReconciliations();
  }, []);

  const fetchReconciliations = async () => {
    try {
      const token = localStorage.getItem('token') || 'citrus-synch-mock-token-123';
      const response = await fetch('/api/v1/reconciliation', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Failed to fetch data');
      const data: Reconciliation[] = await response.json();
      
      // Filter out records where either internal or external record is missing
      const completeRecords = data.filter(rec => rec.internalRecordId && rec.externalRecordId);
      
      setRecords(completeRecords);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('reconciliation.deleteConfirm'))) return;
    try {
      const token = localStorage.getItem('token') || 'citrus-synch-mock-token-123';
      const response = await fetch(`/api/v1/reconciliation/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete record');
      }
      
      // Remove from state
      setRecords(records.filter(r => r._id !== id));
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      const token = localStorage.getItem('token') || 'citrus-synch-mock-token-123';
      const response = await fetch(`/api/v1/reconciliation/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (!response.ok) throw new Error('Failed to update status');
      
      const updatedRec = await response.json();
      setRecords(records.map(r => r._id === id ? { ...r, status: updatedRec.status } : r));
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleOpenComments = (rec: Reconciliation) => {
    setSelectedRec(rec);
    setCommentText('');
    setShowCommentModal(true);
  };

  const handleSaveComment = async () => {
    if (!selectedRec || !commentText.trim()) return;
    
    setSavingComment(true);
    try {
      const token = localStorage.getItem('token') || 'citrus-synch-mock-token-123';
      const response = await fetch(`/api/v1/reconciliation/${selectedRec._id}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ comment: commentText })
      });

      if (!response.ok) throw new Error('Failed to save comment');
      
      const updatedRec = await response.json();
      
      // Update local state
      setRecords(records.map(r => r._id === selectedRec._id ? { ...r, comments: updatedRec.comments } : r));
      setSelectedRec({ ...selectedRec, comments: updatedRec.comments });
      setCommentText('');
    } catch (err: any) {
      alert(err.message);
    } finally {
      setSavingComment(false);
    }
  };

  const getPestClass = (variance: number) => {
    return Math.abs(variance) > 10 ? 'text-danger fw-bold' : 'text-success';
  };

  const getCountClass = (variance: number) => {
    return Math.abs(variance) > 20 ? 'text-danger fw-bold' : 'text-success';
  };

  // Identify duplicates by grouping Block + Date
  const identifyDuplicates = (recs: Reconciliation[]) => {
    const counts: Record<string, number> = {};
    recs.forEach(r => {
      const key = `${r.blockId}-${new Date(r.date).toDateString()}`;
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  };

  const duplicateCounts = identifyDuplicates(records);

  if (loading) return <Container className="text-center py-5"><Spinner animation="border" /></Container>;
  if (error) return <Container className="py-5"><Alert variant="danger">{error}</Alert></Container>;

  return (
    <Container className="py-4">
      <h2 className="mb-4">{t('reconciliation.heading')}</h2>
      <p className="text-muted">{t('reconciliation.subheading')}</p>
      
      <Table striped bordered hover responsive className="mt-4">
        <thead className="table-dark">
          <tr>
            <th>{t('reconciliation.block')}</th>
            <th>{t('reconciliation.date')}</th>
            <th>{t('reconciliation.internalPest')}</th>
            <th>{t('reconciliation.externalPest')}</th>
            <th>{t('reconciliation.pestVariance')}</th>
            <th>{t('reconciliation.internalCount')}</th>
            <th>{t('reconciliation.externalCount')}</th>
            <th>{t('reconciliation.countVariance')}</th>
            <th>{t('reconciliation.status')}</th>
            <th>{t('reconciliation.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {records.length === 0 ? (
            <tr><td colSpan={10} className="text-center">{t('reconciliation.noRecords')}</td></tr>
          ) : (
            records.map(rec => {
              const key = `${rec.blockId}-${new Date(rec.date).toDateString()}`;
              const isDuplicate = duplicateCounts[key] > 1;

              return (
                <tr key={rec._id} className={isDuplicate ? 'table-warning' : ''}>
                  <td>
                    {rec.blockId}
                    {isDuplicate && <Badge bg="warning" text="dark" className="ms-2">{t('reconciliation.duplicate')}</Badge>}
                  </td>
                  <td>{formatDate(rec.date)}</td>
                  <td>{rec.internalRecordId?.pestInfestation}%</td>
                  <td>{rec.externalRecordId?.pestInfestation}%</td>
                  <td className={getPestClass(rec.variance.pestInfestation)}>
                    {rec.variance.pestInfestation > 0 ? '+' : ''}{rec.variance.pestInfestation}%
                  </td>
                  <td>{rec.internalRecordId?.insectCount}</td>
                  <td>{rec.externalRecordId?.insectCount}</td>
                  <td className={getCountClass(rec.variance.insectCount)}>
                    {rec.variance.insectCount > 0 ? '+' : ''}{rec.variance.insectCount}
                  </td>
                  <td>
                    <Form.Select 
                      size="sm" 
                      value={rec.status} 
                      onChange={(e) => handleStatusChange(rec._id, e.target.value)}
                      style={{ minWidth: '120px' }}
                    >
                      <option value="Pending">{t('reconciliation.pending')}</option>
                      <option value="Investigated">{t('reconciliation.investigated')}</option>
                      <option value="Resolved">{t('reconciliation.resolved')}</option>
                    </Form.Select>
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button 
                        variant="secondary" 
                        size="sm"
                        onClick={() => handleOpenComments(rec)}
                      >
                        {t('reconciliation.comments', { count: rec.comments?.length || 0 })}
                      </Button>
                      {isDuplicate && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDelete(rec._id)}
                        >
                          {t('common.delete')}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>

      <Modal show={showCommentModal} onHide={() => setShowCommentModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t('reconciliation.commentsTitle', { blockId: selectedRec?.blockId })}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-4" style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {selectedRec?.comments && selectedRec.comments.length > 0 ? (
              selectedRec.comments.map((c, i) => (
                <div key={i} className="mb-3 p-2 bg-light rounded">
                  <div className="d-flex justify-content-between text-muted small">
                    <strong>{c.author}</strong>
                    <span>{formatDateTime(c.date)}</span>
                  </div>
                  <p className="mb-0 mt-1">{c.text}</p>
                </div>
              ))
            ) : (
              <p className="text-muted fst-italic">{t('reconciliation.noComments')}</p>
            )}
          </div>
          
          <Form.Group>
            <Form.Label>{t('reconciliation.addComment')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder={t('reconciliation.commentPlaceholder')}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCommentModal(false)}>
            {t('reconciliation.close')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveComment}
            disabled={savingComment || !commentText.trim()}
          >
            {savingComment ? t('reconciliation.savingComment') : t('reconciliation.saveComment')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ReconciliationDashboard;
